import { CollectionConfig } from "@rebasepro/types";
import jobsCollection from "./jobs";

/**
 * What a vacancy does on the public board: how often it was opened, and how
 * often somebody pressed its apply control.
 *
 * One row per vacancy, two counters, upserted in place by
 * `POST /api/vacancy-traffic/:id/:kind` (backend `services/vacancy-traffic.ts`)
 * on the owner connection. Not on `jobs` itself, although `jobs.view_count` and
 * `apply_count` were meant for exactly this: an UPDATE of a vacancy fires its
 * `updated_at` trigger and serialises the whole 8 KB row into a realtime event,
 * so a page view would read as an edit and wake every open vacancy list. See
 * drizzle/0068.
 *
 * Counting started on 2026-09-21 (`TRAFFIC_COUNTED_SINCE`). No row means no
 * visits since then, which is a real zero; it does not mean "no data".
 *
 * Read-only everywhere. A hand-edited counter is indistinguishable from a real
 * one, so the panel shows these on the vacancy's cover and nowhere else.
 */
const jobTrafficCollection: CollectionConfig = {
    name: "Tráfico de vacantes",
    singularName: "Tráfico de una vacante",
    slug: "job-traffic",
    table: "job_traffic",
    securityRules: [
        {
            // The employer reads its own vacancies' figures — the dashboard's
            // "¿la ve alguien?". Through the vacancy, because that is where
            // ownership lives; `company_read_own_jobs` already scopes the
            // subquery to the caller.
            name: "company_read_own_traffic",
            operation: "select",
            using: "EXISTS (SELECT 1 FROM jobs j JOIN company_members cm ON cm.company_id = j.company_id WHERE j.id = job_traffic.job_id AND cm.auth_user_id = auth.uid())"
        },
        {
            // No write rule for anyone else, on purpose: the only writer is the
            // recording route, and it writes as the table owner.
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        job_id: {
            name: "Vacante",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => jobsCollection,
                localKey: "job_id"
            },
            validation: { required: true },
            admin: { readOnly: true }
        },
        views: {
            name: "Vistas",
            type: "number",
            description: "Sesiones de navegador que abrieron la vacante, una vez por sesión. Visitas, no personas. Desde el 21 sep 2026.",
            validation: { integer: true },
            admin: { readOnly: true }
        },
        apply_clicks: {
            name: "Clics en postular",
            type: "number",
            description: "Sesiones que pulsaron el botón de postular, lleve a donde lleve. Un clic, no una candidatura.",
            validation: { integer: true },
            admin: { readOnly: true }
        },
        first_recorded_at: {
            name: "Primera visita registrada",
            type: "date",
            admin: { readOnly: true }
        },
        last_recorded_at: {
            name: "Última visita registrada",
            type: "date",
            admin: { readOnly: true }
        }
    },
    admin: {
        // Read on the vacancy's cover (views/JobOverview), which is the only
        // place the question is asked. A flat list of counters across every
        // vacancy answers nothing anybody works from.
        hideFromNavigation: true,
        group: "Vacantes",
        icon: "Visibility",
        sort: ["last_recorded_at", "desc"],
        listProperties: ["job_id", "views", "apply_clicks", "last_recorded_at"],
        previewProperties: ["job_id", "views", "apply_clicks"]
    }
};

export default jobTrafficCollection;
